import { useState } from 'react';
import { MoreHorizontal, Crown, Shield, User, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useUpdateMemberRole, useRemoveMember, useLeaveTeam } from '../../api/hooks/useTeams';
import { ConfirmModal } from '../modals/ConfirmModal';
import { Badge } from '../feedback/Badge';
import type { TeamMember, TeamRole } from '../../types';

interface MemberListProps {
  members: TeamMember[];
  currentUserId: number;
  userRole: TeamRole;
  teamId: number;
  onTransferOwnership: () => void;
}

const roleIcons: Record<TeamRole, React.ReactNode> = {
  owner: <Crown size={14} className="text-yellow-500" />,
  admin: <Shield size={14} className="text-blue-500" />,
  member: <User size={14} className="text-text-secondary" />,
};

const roleLabels: Record<TeamRole, string> = {
  owner: 'Owner',
  admin: 'Admin',
  member: 'Member',
};

export function MemberList({ members, currentUserId, userRole, teamId, onTransferOwnership }: MemberListProps) {
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const [removingMember, setRemovingMember] = useState<TeamMember | null>(null);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const updateRole = useUpdateMemberRole();
  const removeMember = useRemoveMember();
  const leaveTeam = useLeaveTeam();

  const isOwner = userRole === 'owner';
  const isAdmin = userRole === 'admin' || isOwner;

  const handleChangeRole = async (member: TeamMember, newRole: 'admin' | 'member') => {
    await updateRole.mutateAsync({ teamId, userId: member.id, data: { role: newRole } });
    setOpenMenuId(null);
  };

  const handleRemoveMemberClick = (member: TeamMember) => {
    setRemovingMember(member);
    setOpenMenuId(null);
  };

  const handleRemoveMemberConfirm = async () => {
    if (removingMember) {
      await removeMember.mutateAsync({ teamId, userId: removingMember.id });
      setRemovingMember(null);
    }
  };

  const handleLeaveTeamClick = () => {
    setShowLeaveConfirm(true);
    setOpenMenuId(null);
  };

  const handleLeaveTeamConfirm = async () => {
    await leaveTeam.mutateAsync(teamId);
    setShowLeaveConfirm(false);
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="divide-y divide-border">
      {members.map((member) => {
        const isCurrentUser = member.id === currentUserId;
        const isMemberOwner = member.role === 'owner';
        const canManage = isAdmin && !isMemberOwner && !isCurrentUser;
        const canLeave = isCurrentUser && !isMemberOwner;

        return (
          <div
            key={member.id}
            className="flex items-center justify-between px-4 py-3 hover:bg-background-elevated/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              {member.avatar_url ? (
                <img
                  src={member.avatar_url}
                  alt={member.name}
                  className="w-10 h-10 rounded-full object-cover"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium">
                  {getInitials(member.name)}
                </div>
              )}
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-text-primary">
                    {member.name}
                    {isCurrentUser && (
                      <span className="text-text-secondary font-normal"> (you)</span>
                    )}
                  </span>
                </div>
                <div className="text-sm text-text-secondary">{member.email}</div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Badge variant="default" pill={false} icon={roleIcons[member.role]}>
                {roleLabels[member.role]}
              </Badge>

              {(canManage || canLeave) && (
                <div className="relative">
                  <button
                    onClick={() => setOpenMenuId(openMenuId === member.id ? null : member.id)}
                    className="p-1.5 rounded-lg hover:bg-background-elevated transition-colors"
                  >
                    <MoreHorizontal size={18} className="text-text-secondary" />
                  </button>

                  <AnimatePresence>
                    {openMenuId === member.id && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ duration: 0.1 }}
                        className="absolute right-0 top-full mt-1 w-48 bg-background-card rounded-lg border border-border shadow-lg overflow-hidden z-50"
                      >
                        {canManage && (
                          <>
                            {isOwner && (
                              <>
                                {member.role === 'member' ? (
                                  <button
                                    onClick={() => handleChangeRole(member, 'admin')}
                                    disabled={updateRole.isPending}
                                    className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-background-elevated transition-colors disabled:opacity-50"
                                  >
                                    {updateRole.isPending ? (
                                      <Loader2 size={16} className="animate-spin" />
                                    ) : (
                                      <Shield size={16} className="text-blue-500" />
                                    )}
                                    <span className="text-text-primary">Make Admin</span>
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => handleChangeRole(member, 'member')}
                                    disabled={updateRole.isPending}
                                    className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-background-elevated transition-colors disabled:opacity-50"
                                  >
                                    {updateRole.isPending ? (
                                      <Loader2 size={16} className="animate-spin" />
                                    ) : (
                                      <User size={16} className="text-text-secondary" />
                                    )}
                                    <span className="text-text-primary">Make Member</span>
                                  </button>
                                )}
                                <button
                                  onClick={() => {
                                    setOpenMenuId(null);
                                    onTransferOwnership();
                                  }}
                                  className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-background-elevated transition-colors"
                                >
                                  <Crown size={16} className="text-yellow-500" />
                                  <span className="text-text-primary">Transfer Ownership</span>
                                </button>
                              </>
                            )}
                            <button
                              onClick={() => handleRemoveMemberClick(member)}
                              disabled={removeMember.isPending}
                              className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-error hover:bg-error/10 transition-colors disabled:opacity-50"
                            >
                              {removeMember.isPending ? (
                                <Loader2 size={16} className="animate-spin" />
                              ) : null}
                              <span>Remove from Team</span>
                            </button>
                          </>
                        )}
                        {canLeave && (
                          <button
                            onClick={handleLeaveTeamClick}
                            disabled={leaveTeam.isPending}
                            className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-error hover:bg-error/10 transition-colors disabled:opacity-50"
                          >
                            {leaveTeam.isPending ? (
                              <Loader2 size={16} className="animate-spin" />
                            ) : null}
                            <span>Leave Team</span>
                          </button>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </div>
          </div>
        );
      })}

      {members.length === 0 && (
        <div className="px-4 py-8 text-center text-text-secondary">
          No members yet
        </div>
      )}

      <ConfirmModal
        isOpen={removingMember !== null}
        title="Remove Member"
        message={`Are you sure you want to remove ${removingMember?.name} from the team? They will lose access immediately.`}
        confirmText="Remove Member"
        confirmVariant="danger"
        onConfirm={handleRemoveMemberConfirm}
        onClose={() => setRemovingMember(null)}
        isLoading={removeMember.isPending}
      />

      <ConfirmModal
        isOpen={showLeaveConfirm}
        title="Leave Team"
        message="Are you sure you want to leave this team? You will need to be re-invited to regain access."
        confirmText="Leave Team"
        confirmVariant="danger"
        onConfirm={handleLeaveTeamConfirm}
        onClose={() => setShowLeaveConfirm(false)}
        isLoading={leaveTeam.isPending}
      />
    </div>
  );
}
